import React, { useState } from "react";
import { base44 } from "@/api/base44Client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { 
  Mail,
  MapPin,
  Clock,
  CheckCircle,
  Send
} from "lucide-react";

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    interest: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      setError("");
      
      // 1. Sauvegarde dans la base de données (PRIORITAIRE)
      await base44.entities.Contact.create({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        interest: formData.interest,
        message: formData.message,
        status: "nouveau"
      });
      
      // 2. Envoi emails (ne bloque pas si erreur)
      Promise.all([
        // Email vers vous
        base44.integrations.Core.SendEmail({
          to: "contact@amsweb.fr",
          subject: `🔔 Nouveau Contact - ${formData.interest.toUpperCase()} - ${formData.name}`,
          body: `
📬 NOUVEAU CONTACT depuis AmsWeb.fr

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
👤 INFORMATIONS CLIENT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Nom complet: ${formData.name}
📧 Email: ${formData.email}
📱 Téléphone: ${formData.phone || "Non renseigné"}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🎯 INTÉRÊT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Opportunité: ${formData.interest === "wefi" ? "WeFi - Staking & MLM" : "BitNest - Cloud Mining"}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
💬 MESSAGE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${formData.message}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🔗 Lien d'inscription à partager:
${formData.interest === "wefi" ? "https://app.wefi.co/register?ref=p9ybxqycq2" : "https://bitnest.me/X9S6L"}

⚠️ ACTION REQUISE: Contacter ce prospect rapidement !
          `
        }),
        // Email de confirmation au prospect
        base44.integrations.Core.SendEmail({
          from_name: "Fred d'Amsweb",
          to: formData.email,
          subject: "✅ Message bien reçu - Je vous recontacte très vite !",
          body: `
Bonjour ${formData.name},

Merci pour votre message concernant ${formData.interest === "wefi" ? "WeFi (Staking & MLM)" : "BitNest (Cloud Mining)"} ! 🎯

Votre demande a bien été enregistrée et je vais vous recontacter dans les plus brefs délais (généralement sous 24h, souvent bien plus rapide).

En attendant, voici le lien d'inscription direct si vous souhaitez déjà créer votre compte :
${formData.interest === "wefi" ? "👉 https://app.wefi.co/register?ref=p9ybxqycq2" : "👉 https://bitnest.me/X9S6L"}

💡 Astuce : Pensez à vérifier vos spams, ma réponse pourrait s'y retrouver !

Bon Succès !
Fred d'Amsweb
contact@amsweb.fr
          `
        })
      ]).catch(err => console.log("Emails envoyés en arrière-plan"));

      setSuccess(true);
      setFormData({ name: "", email: "", phone: "", interest: "", message: "" });
      setTimeout(() => setSuccess(false), 5000);
    } catch (error) {
      console.error("Erreur lors de l'envoi:", error);
      setError(`Erreur: ${error.message}. Le formulaire n'a pas pu être envoyé.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-900 to-purple-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4">
          <Badge className="mb-6 bg-blue-500/20 text-blue-200 border-blue-400">
            📞 Contactez-nous
          </Badge>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Parlons de Votre Projet
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl">
            Que vous soyez débutant ou expérimenté, nous sommes là pour vous guider 
            vers la meilleure opportunité MLM crypto pour vous
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Info Cards */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="border-0 shadow-xl">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                    <Mail className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">Email</h3>
                  <p className="text-slate-600">contact@amsweb.fr</p>
                  <p className="text-sm text-slate-500 mt-2">Réponse sous 24h</p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-xl">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <Clock className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">Disponibilité</h3>
                  <p className="text-slate-600">Lun - Ven: 9h - 18h</p>
                  <p className="text-slate-600">Sam: 10h - 16h</p>
                  <p className="text-sm text-slate-500 mt-2">Fuseau horaire: Paris (CET)</p>
                </CardContent>
              </Card>

              {/* BONUS EXCLUSIF Card */}
              <Card className="border-0 shadow-xl bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                      <span className="text-xl">💰</span>
                    </div>
                    <h3 className="font-bold text-slate-900 text-lg">BONUS EXCLUSIF</h3>
                  </div>
                  
                  <div className="mb-3">
                    <p className="text-sm text-slate-700 mb-3 flex items-start gap-1">
                      <span>🎯</span>
                      <span>La 1ère personne inscrite ce mois-ci via mon lien de parrainage reçoit :</span>
                    </p>
                  </div>

                  <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-4 text-center mb-3">
                    <p className="text-3xl font-black text-white mb-1">
                      50$ USDT
                    </p>
                    <p className="text-xs font-bold text-green-100">
                      BEP-20 (Binance Smart Chain)
                    </p>
                  </div>

                  <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-lg p-2 text-center">
                    <p className="text-xs font-bold text-slate-900 flex items-center justify-center gap-1">
                      <span>⚡</span>
                      <span>Offre limitée - 1 gagnant/mois</span>
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-0 shadow-2xl">
                <CardHeader className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                  <CardTitle className="text-2xl">Formulaire de Contact</CardTitle>
                  <p className="text-blue-100">Remplissez le formulaire ci-dessous et nous vous recontacterons rapidement</p>
                </CardHeader>
                <CardContent className="p-8">
                  {success && (
                    <div className="mb-6 p-4 bg-green-50 border-2 border-green-300 rounded-lg">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-6 h-6 text-green-600 mt-0.5" />
                        <div>
                          <p className="font-bold text-green-900">Message envoyé avec succès !</p>
                          <p className="text-sm text-green-700">Nous vous recontacterons sous 24h. Vérifiez vos emails (et spams).</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {error && (
                    <div className="mb-6 p-4 bg-red-50 border-2 border-red-300 rounded-lg">
                      <p className="font-bold text-red-900">{error}</p>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name">Nom complet *</Label>
                        <Input
                          id="name"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Jean Dupont"
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="jean.dupont@email.com"
                          className="mt-2"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="phone">Téléphone (optionnel)</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="+33 6 12 34 56 78"
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label htmlFor="interest">Opportunité d'intérêt *</Label>
                        <Select
                          required
                          value={formData.interest}
                          onValueChange={(value) => setFormData({ ...formData, interest: value })}
                        >
                          <SelectTrigger className="mt-2">
                            <SelectValue placeholder="Choisissez une opportunité" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="wefi">WeFi - DeoBank & Staking</SelectItem>
                            <SelectItem value="bitnest">BitNest - Smart Contract DeFi</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message">Votre message *</Label>
                      <Textarea
                        id="message"
                        required
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder="Décrivez votre projet, vos objectifs, vos questions..."
                        rows={6}
                        className="mt-2"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold text-lg py-6"
                    >
                      {isSubmitting ? (
                        "Envoi en cours..."
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Envoyer le message
                        </>
                      )}
                    </Button>

                    <p className="text-sm text-slate-500 text-center">
                      En soumettant ce formulaire, vous acceptez d'être recontacté par email ou téléphone
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
